import {useState, type FC} from "react";
import {ArrowUpIcon, User, Bot, Trash2} from "lucide-react";
import * as m from "@/paraglide/messages";
import {Button} from "@/components/ui/button";
import {
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  AssistantIf,
  ErrorPrimitive,
  useAssistantState,
} from "@assistant-ui/react";
import {MarkdownText} from "@/components/assistant-ui/markdown-text";
import {TooltipIconButton} from "@/components/assistant-ui/tooltip-icon-button";
import {SupportingMentions} from "./supporting-mentions";
import type {SupportingMention, AiPersona} from "@/lib/personas-api";

interface PersonaChatThreadProps {
  profile: AiPersona;
  onClearHistory?: () => void;
}

export const PersonaChatThread: FC<PersonaChatThreadProps> = ({profile, onClearHistory}) => {
  return (
    <ThreadPrimitive.Root
      className="aui-root aui-thread-root flex h-full min-h-0 flex-col"
      style={{
        ["--thread-max-width" as string]: "100%",
      }}
    >
      <ThreadPrimitive.Viewport className="aui-thread-viewport relative flex min-h-0 flex-1 flex-col overflow-y-auto scroll-smooth">
        <AssistantIf condition={({thread}) => thread.isEmpty}>
          <PersonaChatWelcome profile={profile} />
        </AssistantIf>

        <ThreadPrimitive.Messages
          components={{
            UserMessage: PersonaUserMessage,
            AssistantMessage: PersonaAssistantMessage,
          }}
        />

        {/* Spacer for scroll */}
        <div className="min-h-4 flex-shrink-0" />
      </ThreadPrimitive.Viewport>

      <PersonaChatComposer profile={profile} onClearHistory={onClearHistory} />
    </ThreadPrimitive.Root>
  );
};

interface PersonaChatWelcomeProps {
  profile: AiPersona;
}

const PersonaChatWelcome: FC<PersonaChatWelcomeProps> = ({profile}) => {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
      <div className="from-muted to-muted/50 ring-border/50 mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br ring-1">
        <Bot className="text-muted-foreground h-7 w-7" />
      </div>
      <p className="text-muted-foreground max-w-xs text-sm">
        {m.persona_chat_welcome({name: profile.name})}
      </p>
      <PersonaChatSuggestions profile={profile} />
    </div>
  );
};

interface PersonaChatSuggestionsProps {
  profile: AiPersona;
}

const PersonaChatSuggestions: FC<PersonaChatSuggestionsProps> = ({profile}) => {
  const suggestions = [
    m.persona_chat_suggestion_events({name: profile.name}),
    m.persona_chat_suggestion_preferences(),
  ];

  return (
    <div className="mt-4 flex flex-wrap justify-center gap-2">
      {suggestions.map((suggestion, index) => (
        <ThreadPrimitive.Suggestion key={index} prompt={suggestion} send asChild>
          <Button variant="outline" size="sm" className="h-auto px-3 py-2 text-xs">
            {suggestion}
          </Button>
        </ThreadPrimitive.Suggestion>
      ))}
    </div>
  );
};

const PersonaUserMessage: FC = () => {
  return (
    <MessagePrimitive.Root
      className="fade-in slide-in-from-bottom-1 animate-in flex justify-end px-2 py-2"
      data-role="user"
    >
      <div className="flex max-w-[85%] items-start gap-2">
        <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5">
          <MessagePrimitive.Parts />
        </div>
        <div className="bg-muted ring-border/50 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1">
          <User className="text-muted-foreground h-4 w-4" />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};

const PersonaAssistantMessage: FC = () => {
  const mentions = useAssistantState(
    (state) =>
      state.message.metadata?.custom?.supporting_mentions as SupportingMention[] | undefined
  );

  return (
    <MessagePrimitive.Root
      className="fade-in slide-in-from-bottom-1 animate-in px-2 py-2"
      data-role="assistant"
    >
      <div className="flex max-w-[85%] items-start gap-2">
        <div className="bg-muted ring-border/50 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1">
          <Bot className="text-muted-foreground h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="bg-muted/50 text-foreground rounded-2xl rounded-tl-sm px-4 py-2.5">
            <MessagePrimitive.Parts
              components={{
                Text: MarkdownText,
              }}
            />
            <MessageError />
          </div>
          {/* Supporting mentions shown below message - only when complete and has sources */}
          <AssistantIf condition={({message}) => message.status?.type !== "running"}>
            {mentions?.length ? (
              <div className="mt-2">
                <SupportingMentions mentions={mentions} />
              </div>
            ) : null}
          </AssistantIf>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
};

const MessageError: FC = () => {
  return (
    <MessagePrimitive.Error>
      <ErrorPrimitive.Root className="border-destructive bg-destructive/10 text-destructive dark:bg-destructive/5 mt-2 rounded-md border p-2 text-xs dark:text-red-200">
        <ErrorPrimitive.Message className="line-clamp-2" />
      </ErrorPrimitive.Root>
    </MessagePrimitive.Error>
  );
};

interface PersonaChatComposerProps {
  profile: AiPersona;
  onClearHistory?: () => void;
}

const PersonaChatComposer: FC<PersonaChatComposerProps> = ({profile, onClearHistory}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleClear = () => {
    onClearHistory?.();
    setShowClearConfirm(false);
  };

  return (
    <div className="border-border/50 shrink-0 border-t pt-3">
      <ComposerPrimitive.Root className="relative flex w-full items-end gap-2">
        {/* Clear history button */}
        {onClearHistory && (
          <div className="relative">
            {showClearConfirm ? (
              <div className="bg-destructive/10 border-destructive/20 flex items-center gap-1 rounded-lg border p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/20 h-7 px-2 text-xs"
                  onClick={handleClear}
                >
                  {m.persona_chat_confirm_clear()}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setShowClearConfirm(false)}
                >
                  {m.chat_action_cancel()}
                </Button>
              </div>
            ) : (
              <TooltipIconButton
                tooltip={m.persona_chat_clear()}
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive h-9 w-9"
                onClick={() => setShowClearConfirm(true)}
              >
                <Trash2 className="h-4 w-4" />
              </TooltipIconButton>
            )}
          </div>
        )}

        {/* Input field */}
        <div className="bg-muted/50 ring-border focus-within:ring-ring/20 flex min-h-[44px] flex-1 items-center rounded-xl ring-1 transition-shadow focus-within:ring-2">
          <ComposerPrimitive.Input
            placeholder={m.persona_chat_placeholder({name: profile.name})}
            className="placeholder:text-muted-foreground min-h-[44px] w-full resize-none bg-transparent px-4 py-3 text-sm outline-none"
            rows={1}
            autoFocus
          />
        </div>

        {/* Send button */}
        <AssistantIf condition={({thread}) => !thread.isRunning}>
          <ComposerPrimitive.Send asChild>
            <TooltipIconButton
              tooltip={m.chat_send_message()}
              variant="default"
              size="icon"
              className="h-9 w-9 rounded-full"
            >
              <ArrowUpIcon className="h-4 w-4" />
            </TooltipIconButton>
          </ComposerPrimitive.Send>
        </AssistantIf>

        <AssistantIf condition={({thread}) => thread.isRunning}>
          <Button variant="secondary" size="icon" className="h-9 w-9 rounded-full" disabled>
            <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
          </Button>
        </AssistantIf>
      </ComposerPrimitive.Root>
    </div>
  );
};
